// Module-level "modes already peeked this tab session" store.
//
// Lifted out of `useActivityDockModePeek`'s `useRef<Set<PrimaryMode>>`
// so the set is readable + clearable from outside the hook (the
// dev-only peek-readiness probe surfaces it as a diagnostic, and
// the new "Clear peeked modes" button lets a dev reset dedup
// without a tab reload). Production behavior unchanged: the dock
// still tracks exactly one peek per (mode, session) — the storage
// just moved from a per-hook ref to a per-tab module singleton.
//
// Why module scope + not React state / context:
//   - The dock mounts exactly once per tab (`LeadWorkspaceShell`
//     mounts `ActivityFloatingDock` at the shell root). Hoisting
//     the set to module scope makes the production semantics
//     identical to the pre-existing ref + saves a context layer.
//   - Dev consumers (the probe) can subscribe from any tree depth
//     without forcing the dock to publish via React context.
//   - The store resets naturally on tab reload (module re-eval),
//     matching the pre-existing "session" definition.
//
// Tests reset the store in their setup (`beforeEach(clearPeekedModes)`)
// because module-level state would otherwise leak between cases.

import type { PrimaryMode } from "@/components/lead/sections/LeadWorkspaceHeaderAccess";

const peekedModes = new Set<PrimaryMode>();
type Listener = (snapshot: ReadonlySet<PrimaryMode>) => void;
const listeners = new Set<Listener>();

/** Snapshot of the current peeked-modes set. Caller must not mutate. */
export function getPeekedModes(): ReadonlySet<PrimaryMode> {
  return peekedModes;
}

/** Has this mode already been peeked this session? Equivalent to the
 * pre-lift `peekedModesRef.current.has(mode)` check. */
export function hasPeekedMode(mode: PrimaryMode): boolean {
  return peekedModes.has(mode);
}

/** Mark a mode as peeked. Idempotent. Notifies subscribers. */
export function addPeekedMode(mode: PrimaryMode): void {
  if (peekedModes.has(mode)) return;
  peekedModes.add(mode);
  notify();
}

/** Forget every peek. Used by the dev probe's "Clear peeked modes"
 * button so a developer can re-trigger peeks without a tab reload,
 * and by tests in `beforeEach` to keep cases independent. Notifies
 * subscribers. */
export function clearPeekedModes(): void {
  if (peekedModes.size === 0) return;
  peekedModes.clear();
  notify();
}

/** Subscribe to changes. Returns an unsubscribe function. Snapshot
 * is the same `peekedModes` set reference on every notification —
 * subscribers that need referential change-detection should diff
 * their own copy. */
export function subscribePeekedModes(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function notify(): void {
  for (const listener of listeners) listener(peekedModes);
}
